import { useMemo } from "react";
import { useDataFetch } from "./Use-data-fetch";

/**
 * Directos activos que exige un rango cuando la base de datos no lo define.
 *
 * Regla de negocio: los tres primeros rangos piden 3, y de ahí en adelante el
 * requisito es la posición del rango — el cuarto pide 4, el quinto 5, y así.
 *
 * `required_active_users` del documento manda siempre que exista; esto sólo
 * cubre los rangos que se crearon antes de que ese campo existiera.
 *
 * @param position Posición del rango, empezando en 1, ordenado de menor a mayor puntaje
 */
export const resolveRequiredActiveUsers = (rank, position) => {
  if (rank?.required_active_users !== undefined && rank?.required_active_users !== null) {
    return rank.required_active_users;
  }

  return position <= 3 ? 3 : position;
};

export const useRanks = (userId = null) => {
  const { getData } = useDataFetch();

  const { data, isLoading: ranksLoading, error } = getData(
    ["ranks"],
    "/ranks"
  );

  const { data: myRanksData, isLoading: myRanksLoading } = getData(
    ["my-ranks", userId],
    `/ranks/dashboard/my-ranks${userId ? `?userId=${userId}` : ''}`,
    { enabled: true }
  );

  const { data: loyaltyData, isLoading: loyaltyLoading } = getData(
    ["loyalty-bonus-monthly", userId],
    `/order/dashboard/calculate/monthly/loyalty_points${userId ? `?userId=${userId}` : ''}`,
    { enabled: true }
  );

  const isLoading = ranksLoading || myRanksLoading || loyaltyLoading;

  const currentUserPoints = useMemo(() => {
    if (!loyaltyData?.data?.pointsData) return 0;

    const { pointsData, levels } = loyaltyData.data;
    const hasEnoughActiveUsers = pointsData.levelOneActive >= 3;

    let totalPoints = 0;

    levels.forEach((level) => {
      const levelNumber = parseInt(level.name);
      const isLocked = !hasEnoughActiveUsers && levelNumber >= 4;

      if (!isLocked) {
        // En vivo (`ordersByLevel`) y no `totalLoyaltyPointsByLevel`, que
        // llega pisado por la caché de puntos: si no, "Puntos Actuales" y el
        // rango quedaban por debajo de la tabla de niveles de la misma vista.
        const pointsByCurrency = pointsData.ordersByLevel?.[level.name]?.points_by_currency || {
          COP: 0,
          USDT: 0,
          EUR: 0,
          USD: 0
        };
        const levelPoints = Object.values(pointsByCurrency).reduce((sum, points) => sum + points, 0);
        totalPoints += levelPoints;
      }
    });

    return totalPoints;
  }, [loyaltyData]);

  /**
   * Directos activos del usuario, ahora mismo.
   *
   * `levelOneActive` es el conteo de directos con lealtad vigente que ya
   * calcula order-ms, y es el mismo número que el motor de liquidación usa
   * como `active_level1_users`. Se expone para que las vistas no tengan que
   * leerlo de un corte guardado.
   */
  const activeDirects = loyaltyData?.data?.pointsData?.levelOneActive ?? 0;

  const sortedRanks = useMemo(() => {
    const ranks = (data?.data?.ranks || [])
      .slice()
      .sort((a, b) => a.points - b.points);

    const myRanks = myRanksData?.data || [];

    // El requisito de directos activos se resuelve una sola vez acá, así todos
    // los consumidores del hook leen el mismo número y ninguno tiene que
    // conocer la regla de respaldo.
    const ranksWithRequirement = ranks.map((rank, index) => ({
      ...rank,
      required_active_users: resolveRequiredActiveUsers(rank, index + 1),
    }));

    // Primer corte (el más antiguo) en que el usuario alcanzó cada rango. Un
    // corte con un rango superior también cuenta, así los rangos que se saltó
    // quedan con la fecha en que los superó. Las recalificaciones posteriores
    // no reemplazan ni la fecha ni el puntaje de ese primer logro.
    const cutTime = (item) =>
      new Date(item.period?.start ?? item.calculation_date ?? 0).getTime();

    const qualifyingCuts = myRanks
      .filter((item) => item.user_data?.rank?.qualifies !== false)
      .sort((a, b) => cutTime(a) - cutTime(b));

    return ranksWithRequirement.map((rank) => ({
      ...rank,
      myRank:
        qualifyingCuts.find(
          (item) =>
            (item.user_data?.rank?.points_required || 0) >= rank.points
        ) ?? null,
    }));
  }, [data, myRanksData]);

  const { currentRank, currentRankIndex } = useMemo(() => {
    let index = -1;

    sortedRanks.forEach((rank, i) => {
      if (rank.myRank) {
        index = i;
      }
    });

    return {
      currentRank:
        index >= 0
          ? sortedRanks[index].myRank
          : undefined,
      currentRankIndex: index,
    };
  }, [sortedRanks]);

  const nextRank =
    currentRankIndex >= 0
      ? sortedRanks[currentRankIndex + 1] || null
      : null;

  const currentRankPoints =
    currentRank?.user_data.rank.points_required || 0;

  const nextRankPoints = nextRank?.points || currentRankPoints;

  const progressToNextRank = useMemo(() => {
    if (!nextRank) return 100;

    const range = nextRankPoints - currentRankPoints;
    if (range <= 0) return 0;

    const progress = currentUserPoints - currentRankPoints;

    return Math.min(100, Math.max(0, (progress / range) * 100));
  }, [nextRank, currentUserPoints, currentRankPoints, nextRankPoints]);

  const { currentPointsRank, nextPointsRank } = useMemo(() => {
    let currentIndex = -1;

    sortedRanks.forEach((rank, index) => {
      if (currentUserPoints >= rank.points) {
        currentIndex = index;
      }
    });

    return {
      currentPointsRank:
        currentIndex >= 0 ? sortedRanks[currentIndex] : null,
      nextPointsRank:
        currentIndex >= 0
          ? sortedRanks[currentIndex + 1] || null
          : sortedRanks[0] || null,
    };
  }, [sortedRanks, currentUserPoints]);

  const pointsToNextRank = Math.max(
    0,
    nextPointsRank?.points - currentUserPoints
  );

  return {
    ranks: sortedRanks,
    isLoading,
    error,
    currentRankIndex,
    currentUserPoints,
    currentRank,
    nextRank,
    pointsToNextRank,
    progressToNextRank,
    currentPointsRank,
    nextPointsRank,
    activeDirects,
    myRanks: myRanksData?.data || [],
  };
};